import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Snackbar,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  CloseOutlined,
  EditOutlined,
  PublicOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import UserProfile from "../../models/user/UserProfile";
import { currencyOptions, popularCurrencyCodes } from "../../utils/currencies";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import FormField from "../common/ui/FormField";
import AmountInput from "../common/AmountInput";
import SurfaceCard from "../common/ui/SurfaceCard";
import { useUserProfileContext } from "../../contexts/ProfileContext";

const FinancialPreferencesSection: React.FC = () => {
  const theme = useTheme();
  const { setCurrency: setGlobalCurrency, formatCurrencyCodeNumber } = useCurrencyFormatter();

  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const { error, helpText, setError, clearError, handleError } = useError();
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [startingBalanceTouched, setStartingBalanceTouched] = useState(false);

  const [currency, setCurrency] = useState("");
  const [startingBalance, setStartingBalance] = useState("0");

  const { userProfile, refetchProfile, updateProfile, isSavingProfile } = useUserProfileContext();

  useEffect(() => {
    if (userProfile) {
      setCurrency(userProfile.currency);
      setStartingBalance(userProfile.startingBalance.toString());
    }
  }, [userProfile]);

  const handleSave = () => {
    setHasAttemptedSubmit(true);

    if (!currency.trim()) {
      setError("Currency is required.");
      return;
    }

    const startingBalanceError = getStartingBalanceError(startingBalance);
    if (startingBalanceError) {
      setError(startingBalanceError);
      return;
    }

    const parsedBalance = Number(startingBalance);

    const updatedProfile: UserProfile = {
      email: userProfile?.email || "",
      name: userProfile?.name || "",
      currency,
      startingBalance: parsedBalance,
    };

    updateProfile(updatedProfile, {
      onSuccess: () => {
        setSuccess("Financial preferences updated successfully!");
        setEditing(false);
        setGlobalCurrency(currency);
        refetchProfile();
      },
      onError: (apiError) => {
        handleError(apiError);
      },
    });
  };

  const handleCancel = () => {
    setEditing(false);
    setCurrency(userProfile?.currency || "USD");
    setStartingBalance(userProfile?.startingBalance.toString() || "0");
    setHasAttemptedSubmit(false);
    setStartingBalanceTouched(false);
    clearError();
  };

  const getStartingBalanceError = (value: string): string => {
    if (!value || value.trim() === "") return "Starting balance is required.";
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return "Starting balance must be a valid amount.";
    if (parsed === 0) return "Starting balance must be different from 0.";
    return "";
  };

  const startingBalanceError = getStartingBalanceError(startingBalance);
  const showStartingBalanceError = Boolean(startingBalanceError) && (hasAttemptedSubmit || startingBalanceTouched);

  const popularCurrencies = currencyOptions.filter((c) => popularCurrencyCodes.includes(c.code));
  const otherCurrencies = currencyOptions
    .filter((c) => !popularCurrencyCodes.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name));

  const groupedCurrencies = [...popularCurrencies, ...otherCurrencies];
  const previewAmount = formatCurrencyCodeNumber(1234.56);
  const previewStartingBalance = formatCurrencyCodeNumber(Number(startingBalance) || 0);

  return (
    <Box sx={{ p: { xs: 1.2, md: 1.8 } }}>
      <ErrorAlert
        error={error}
        helpText={helpText}
        onClear={clearError}
        onRetry={() => refetchProfile()}
        title="Financial Preferences Error"
      />

      <SurfaceCard
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
        }}
      >
        <Box sx={{ p: { xs: 2, md: 2.6 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2.4 }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Financial Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure currency and starting balance defaults.
              </Typography>
            </Box>

            {!editing ? (
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => setEditing(true)}
                sx={{
                  textTransform: "none",
                  borderColor: alpha(theme.palette.success.main, 0.45),
                  color: theme.palette.success.light,
                }}
              >
                Edit
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveOutlined />}
                  onClick={handleSave}
                  disabled={isSavingProfile}
                  sx={{ textTransform: "none" }}
                >
                  {isSavingProfile ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CloseOutlined />}
                  onClick={handleCancel}
                  disabled={isSavingProfile}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Stack>

          <Autocomplete
            fullWidth
            value={currencyOptions.find((c) => c.code === currency) || null}
            onChange={(_, newValue) => {
              if (newValue) {
                setCurrency(newValue.code);
              }
            }}
            options={groupedCurrencies}
            groupBy={(option) =>
              popularCurrencyCodes.includes(option.code) ? "Popular currencies" : "All currencies"
            }
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: "flex", gap: 1.2, py: 1 }}>
                <Typography sx={{ fontWeight: 700, minWidth: 30 }}>{option.symbol}</Typography>
                <Typography sx={{ fontWeight: 500 }}>{option.name}</Typography>
                <Typography color="text.secondary" sx={{ ml: "auto" }}>
                  {option.code}
                </Typography>
              </Box>
            )}
            disabled={!editing}
            renderInput={(params) => (
              <FormField
                {...params}
                label="Currency"
                placeholder="Search currencies..."
                helperText={
                  editing
                    ? "Search or select your preferred currency."
                    : "Click edit to change currency."
                }
              />
            )}
          />

          <AmountInput
            fullWidth
            value={startingBalance === "0" ? "0.00" : startingBalance}
            onChange={(value) => setStartingBalance(value)}
            allowNegative
            disabled={!editing}
            label="Starting balance"
            placeholder="0.00"
            onBlur={() => setStartingBalanceTouched(true)}
            error={showStartingBalanceError}
            helperText={
              showStartingBalanceError
                ? startingBalanceError
                : "Starting baseline used for account setup and reporting context."
            }
            sx={{ mt: 2.2, mb: 1.4 }}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <PublicOutlined fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="text.secondary">
                Preview: {previewAmount}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <AccountBalanceWalletOutlined fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="text.secondary">
                Current start balance: {previewStartingBalance}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </SurfaceCard>

      <Snackbar
        open={!!success}
        autoHideDuration={4200}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialPreferencesSection;
